import { datadogLogs } from '@datadog/browser-logs';

// Some browsers make SystemJS download the MFE entrypoints through fetch + eval instead of a
// <script> tag, although our SystemJS build only does that for css/json/wasm/html files; the module
// then resolves empty and single-spa fails in LOADING_SOURCE_CODE. Describes the loader so the
// failure log shows what altered it.
const SOURCE_PREVIEW_LENGTH = 300;
const INLINE_SCRIPT_PREVIEW_LENGTH = 80;

type SystemJsLoader = Window['System'] & { shouldFetch?: unknown; fetch?: unknown };

const systemAtBoot = window.System;

const preview = (value: unknown) => String(value).slice(0, SOURCE_PREVIEW_LENGTH);

export const describeSystemJs = () => {
  const system: SystemJsLoader | undefined = window.System;
  const descriptor = Object.getOwnPropertyDescriptor(window, 'System');
  return {
    systemReplacedSinceBoot: system !== systemAtBoot,
    systemIsAccessor: !!descriptor && !('value' in descriptor),
    shouldFetch: preview(system?.shouldFetch),
    fetch: preview(system?.fetch),
    pageScripts: Array.from(document.scripts).map(
      script => script.src || `inline:${(script.textContent ?? '').trim().slice(0, INLINE_SCRIPT_PREVIEW_LENGTH)}`
    ),
  };
};

// Same rule as single-spa's validLifecycleFn: a function or an array of functions.
const isLifecycle = (lifecycle: unknown) =>
  typeof lifecycle === 'function' ||
  (Array.isArray(lifecycle) && lifecycle.every(fn => typeof fn === 'function'));

// Checked here rather than in a single-spa error handler: single-spa sets the new status
// (SKIP_BECAUSE_BROKEN / LOAD_ERROR) before calling its handlers. Logged as warn because the
// Datadog beforeSend drops error logs while the tab is hidden.
// A rejected import still propagates: single-spa needs it to pick LOAD_ERROR.
export const importMfe = async (entrypoint: string) => {
  const mfeModule = await window.System.import(entrypoint);
  try {
    if (!isLifecycle(mfeModule?.mount) || !isLifecycle(mfeModule?.unmount)) {
      datadogLogs.logger.warn(`${entrypoint} resolved without mount/unmount lifecycles`, {
        mfeImport: {
          entrypoint,
          exportedKeys: mfeModule ? Object.keys(mfeModule) : [],
          systemJs: describeSystemJs(),
        },
      });
    }
  } catch {
    // The diagnostic must never change what single-spa receives.
  }
  return mfeModule;
};
